import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { getArticleBySlug, articles as allArticles } from '@/mocks/articles';
import { getCategoryBySlug } from '@/mocks/categories';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : null;
  const category = article ? getCategoryBySlug(article.category) : null;

  const relatedArticles = article
    ? allArticles
        .filter((a) => a.category === article.category && a.slug !== article.slug)
        .slice(0, 3)
    : [];

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-24 pb-16">
          <div className="max-w-6xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-2xl font-heading font-semibold text-foreground-900 mb-4">
              文章未找到
            </h1>
            <Link
              to="/"
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-24 pb-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              ...(category
                ? [{ label: category.name, to: `/category/${category.slug}` }]
                : []),
              { label: article.title },
            ]}
          />

          <header className="mb-10">
            <div className="flex flex-wrap gap-2 mb-4">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-6 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line text-primary-500 text-xs"></i>
                </span>
                <span>
                  <strong className="text-foreground-800">{article.author}</strong>
                  <span className="mx-1 text-foreground-400">·</span>
                  {article.authorTitle}
                </span>
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.publishedAt}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {article.readTime} 分钟阅读
              </span>
            </div>
          </header>

          <div className="mb-10 rounded-lg overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-auto object-cover max-h-[500px]"
            />
          </div>

          <div
            className="prose prose-lg max-w-none
              prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-semibold
              prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-4
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
              prose-strong:text-foreground-900
              [&_p]:text-base [&_p]:md:text-base
              [&_h2]:text-xl [&_h2]:md:text-xl"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-14 pt-8 border-t border-background-200/70">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="text-sm font-medium text-foreground-900 mb-2">标签</h3>
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-600 whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-foreground-500">分享：</span>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 transition-colors cursor-pointer">
                  <i className="ri-wechat-line text-sm text-foreground-600"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 transition-colors cursor-pointer">
                  <i className="ri-weibo-line text-sm text-foreground-600"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 transition-colors cursor-pointer">
                  <i className="ri-link text-sm text-foreground-600"></i>
                </button>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="max-w-4xl mx-auto px-4 md:px-6 mt-16">
            <h3 className="text-xl font-heading font-semibold text-foreground-900 mb-6">
              相关文章
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedArticles.map((a) => (
                <ArticleCard key={a.slug} article={a} />
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}